/**
 * External Game Widget
 *
 * Displays external games with a distinctive banner design.
 * Features the game icon on the left, title+tagline inline on desktop (tagline hidden on mobile),
 * and a Play Now button in the content area below — matches GameCard pattern.
 */

import { handleExternalGameClick } from '@/services/externalGameService';
import { useAuth } from '@/hooks/useAuth';
import { isImageIconUrl } from '@/utils/iconRenderer';
import type { Game } from '@/types/game';

interface ExternalGameWidgetProps {
  game: Game;
}

export function ExternalGameWidget({ game }: ExternalGameWidgetProps) {
  const { requireAuth } = useAuth();

  const handlePlayClick = () => {
    requireAuth(() => {
      handleExternalGameClick(game);
    });
  };

  const iconUrl = game.iconUrl || game.displayName.substring(0, 2).toUpperCase();
  const isImage = isImageIconUrl(iconUrl);

  return (
    <div className="relative bg-[#D2EBF9] dark:bg-sport-baseball-dark border border-[#E6E6E6] dark:border-sport-baseball-primary/30 rounded-xl p-3 md:p-[clamp(10px,1.2vw,14px)] overflow-hidden">
      {/* Sport accent line - matches GameCard styling */}
      <div
        className="absolute inset-y-0 left-0 w-1"
        style={{ backgroundColor: game.signatureColor }}
      />

      <div className="flex items-center gap-[clamp(12px,3vw,16px)] md:gap-[clamp(8px,1vw,14px)]">
        {/* Game Icon - larger on mobile to match GameCard row height visually */}
        <div className="flex-shrink-0">
          {isImage ? (
            <img
              src={iconUrl}
              alt={game.displayName}
              className="w-[clamp(72px,19vw,96px)] h-[clamp(72px,19vw,96px)] md:w-[clamp(90px,12vw,115px)] md:h-[clamp(90px,12vw,115px)] xl:w-[clamp(56px,8vw,88px)] xl:h-[clamp(56px,8vw,88px)] rounded-md object-cover"
            />
          ) : (
            <div
              className="w-[clamp(72px,19vw,96px)] h-[clamp(72px,19vw,96px)] md:w-[clamp(90px,12vw,115px)] md:h-[clamp(90px,12vw,115px)] xl:w-[clamp(56px,8vw,88px)] xl:h-[clamp(56px,8vw,88px)] rounded-md flex items-center justify-center text-white font-bebas-neue text-[clamp(20px,5vw,26px)] md:text-[clamp(16px,1.2vw,28px)]"
              style={{ backgroundColor: game.signatureColor }}
            >
              {iconUrl}
            </div>
          )}
        </div>

        {/* Text + PLAY NOW — title+tagline inline on desktop, button below */}
        <div className="flex-1 min-w-0">
          {/* Title + tagline inline on desktop, tagline hidden on mobile */}
          <div className="flex flex-col md:flex-row md:flex-wrap md:items-baseline gap-y-0 md:gap-x-[clamp(6px,0.6vw,10px)] mb-0.5 md:mb-1">
            <h3 className="font-bebas-neue text-[clamp(16px,4vw,24px)] md:text-[clamp(18px,2.2vw,30px)] xl:text-[clamp(16px,1.4vw,24px)] font-medium tracking-[1.5px] uppercase text-foreground leading-none flex-shrink-0">
              {game.displayName}
            </h3>
            <p className="hidden md:block font-oswald text-[clamp(10px,1vw,14px)] xl:text-[clamp(9px,0.65vw,12px)] font-medium uppercase tracking-[0.5px] text-muted-foreground leading-tight">
              {game.tagline}
            </p>
          </div>

          {/* PLAY NOW — below title row, left-aligned */}
          <button
            onClick={handlePlayClick}
            className="bg-[#1a1a1a] dark:bg-sport-baseball-primary text-white rounded px-[clamp(12px,3vw,18px)] md:px-[clamp(12px,1vw,20px)] py-[clamp(6px,1.5vw,10px)] md:py-[clamp(6px,0.5vw,10px)] font-oswald text-[clamp(11px,2.8vw,14px)] md:text-[clamp(11px,0.8vw,14px)] font-bold uppercase tracking-[0.5px] transition-all hover:-translate-y-px hover:shadow-lg hover:shadow-black/30 dark:hover:shadow-sport-baseball-primary/30 whitespace-nowrap"
          >
            Play Now
          </button>
        </div>
      </div>
    </div>
  );
}